import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type INotification } from '@/types/INotification';
import { useAppDispatch, useAppSelector } from '@/store/reduxHooks';
import {
  markRead,
  markAllRead,
  clearToasts,
  clearHistory,
  pruneStale,
  syncHistory
} from '@/store/notifications';

interface IUseNotificationCenter {
  isEnabled: boolean;
  isOpen: boolean;
  notifications: INotification[];
  unreadCount: number;
  hasUnread: boolean;
  toggle: () => void;
  close: () => void;
  handleSelect: (path: string, id: string) => void;
  handleMarkAllRead: () => void;
  handleClearAll: () => void;
}

/**
 * Единственный тумблер центра уведомлений. Пока `false`, колокольчик выведен
 * из работы: бейдж пуст, панель не открывается. Под уведомления будет
 * отдельный сокет и отдельный сервис — вся логика ниже сохранена и оживает
 * возвратом флага в `true` (плюс возврат панели в `NotificationCenter.tsx`).
 */
const IS_NOTIFICATION_CENTER_ENABLED: boolean = false;

/**
 * Логика центра уведомлений: открытие панели, выбор уведомления (переход +
 * пометка прочитанным), массовые действия. Бейдж считается от непросмотренных
 * заказов (`unviewedOrders`) — единый источник с фавиконом. При открытии
 * панели чистит протухшую историю (ленивая суточная чистка).
 */
export const useNotificationCenter = (): IUseNotificationCenter => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const notifications = useAppSelector((state) => state.notifications.history);
  const unviewedCount = useAppSelector(
    (state) => state.unviewedOrders.uuids.length
  );

  const hasUnread = notifications.some((item) => !item.isRead);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggle = useCallback(() => {
    if (!IS_NOTIFICATION_CENTER_ENABLED) {
      return;
    }

    // dispatch только в обработчике события (не внутри setState-updater'а,
    // иначе обновление стора идёт во время рендера → React-варнинг).
    if (!isOpen) {
      dispatch(syncHistory());
      dispatch(pruneStale());
    }
    setIsOpen((prev) => !prev);
  }, [dispatch, isOpen]);

  const handleSelect = useCallback(
    (path: string, id: string) => {
      dispatch(markRead(id));
      void navigate(path);
      setIsOpen(false);
    },
    [dispatch, navigate]
  );

  const handleMarkAllRead = useCallback(() => {
    dispatch(markAllRead());
  }, [dispatch]);

  const handleClearAll = useCallback(() => {
    dispatch(clearHistory());
    dispatch(clearToasts());
  }, [dispatch]);

  return {
    isEnabled: IS_NOTIFICATION_CENTER_ENABLED,
    isOpen: IS_NOTIFICATION_CENTER_ENABLED && isOpen,
    notifications,
    unreadCount: IS_NOTIFICATION_CENTER_ENABLED ? unviewedCount : 0,
    hasUnread: IS_NOTIFICATION_CENTER_ENABLED && hasUnread,
    toggle,
    close,
    handleSelect,
    handleMarkAllRead,
    handleClearAll
  };
};
